import Vue from 'vue'
import cache from '@/utils/cache'
import { getIntercomRegistration } from '@/utils/api'
import { rtcHelper } from '@/vendor/tr-webrtc.module'

const state = Vue.observable({
  status: '未启动',
  error: '',
  calls: [],
  enabled: false,
  callAllowed: null
})

class IntercomManager {
  constructor() {
    this.calls = new Map()
    this.endWaiters = new Map()
    this.loginTimer = null
    this.audioContext = null
    this.ringTimer = null
    this.remoteMediaSequence = 0
    this.bus = null
    this.started = false
  }

  init(bus) {
    if (this.bus === bus) return
    if (this.bus) this.bus.$off('changeRoad', this.refreshVisibility)
    this.bus = bus
    this.refreshVisibility = this.refreshVisibility.bind(this)
    this.bus.$on('changeRoad', this.refreshVisibility)
  }

  get selectedGateNames() {
    const selections = cache.session.getJSON('selectParkList') || []
    return new Set(selections.flatMap(park => park.gates || [])
      .map(gate => this.normalizeName(gate.gateName))
      .filter(Boolean))
  }

  get selectedGates() {
    const selections = cache.session.getJSON('selectParkList') || []
    return selections.flatMap(park => park.gates || [])
  }

  normalizeName(value) {
    return String(value || '').trim().replace(/\s+/g, '').toLocaleLowerCase()
  }

  getCallerName(dialog) {
    const params = dialog.params || {}
    return params.remote_caller_id_name || params.caller_id_name || ''
  }

  getMatchedGate(dialog) {
    const params = dialog.params || {}
    const callerName = this.getCallerName(dialog)
    const callerNumber = params.remote_caller_id_number || params.caller_id_number || ''
    const bracketedNumber = callerName.match(/[<(（]([^)>）]+)[)>）]/)?.[1] || ''
    const callerAliases = new Set([callerNumber, bracketedNumber].map(value => this.normalizeName(value)).filter(Boolean))
    const name = this.normalizeName(callerName.replace(/\s*[<(（].*$/, ''))
    const matchedGate = this.selectedGates.find(gate => {
      const extension = this.normalizeName(gate.sipExtension)
      if (extension) return callerAliases.has(extension)
      return !!name && this.normalizeName(gate.gateName) === name
    })
    console.info('[语音对讲] 来电通道匹配', {
      callerName,
      callerNumber,
      callerAliases: [...callerAliases],
      selectedGates: this.selectedGates.map(gate => ({
        gateNo: gate.gateNo,
        gateName: gate.gateName,
        sipExtension: gate.sipExtension || ''
      })),
      matchedGate: matchedGate ? {
        gateNo: matchedGate.gateNo,
        gateName: matchedGate.gateName,
        sipExtension: matchedGate.sipExtension || ''
      } : null
    })
    return matchedGate || null
  }

  isSelected(dialog) {
    return !!this.getMatchedGate(dialog)
  }

  async start() {
    if (this.started || state.status === '连接中' || state.status === '准备音频') return
    state.error = ''
    state.status = '准备音频'
    try {
      const registration = (await getIntercomRegistration()).data
      state.callAllowed = registration?.callAllowed === true
      if (!registration?.callAllowed) {
        state.enabled = false
        state.status = '无通话权限'
        return
      }
      this.ensureRTCPeerConnection()
      if (!window.isSecureContext || !navigator.mediaDevices?.getUserMedia) {
        throw new Error('岗亭网页需通过 HTTPS 打开后才能使用麦克风。')
      }
      if (!registration?.signalUrl || !registration?.webExtension || !registration?.password) {
        throw new Error('调度平台网页分机配置不完整。')
      }
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false })
      stream.getTracks().forEach(track => track.stop())
      await this.unlockAudio()
      this.ensureLegacyGetUserMedia()
      this.started = true
      state.enabled = true
      state.status = '连接中'
      rtcHelper.bootstrap(
        registration.webExtension,
        registration.password,
        registration.signalUrl,
        '',
        dialog => this.createRemoteMedia(dialog),
        'intercomLocalAudio',
        this.callbacks,
        'any',
        'any',
        false,
        '',
        ''
      )
      clearTimeout(this.loginTimer)
      this.loginTimer = setTimeout(() => {
        if (state.status === '连接中') {
          state.status = '连接超时'
          state.error = '未能登录网页分机，请检查 WSS、账号密码和 HTTPS 网络环境。'
          this.started = false
        }
      }, 20000)
    } catch (error) {
      this.started = false
      state.enabled = false
      state.status = '未启动'
      state.error = error?.message || '语音值班启动失败。'
    }
  }

  async unlockAudio() {
    const AudioContextClass = window.AudioContext || window.webkitAudioContext
    if (!AudioContextClass) return
    this.audioContext = this.audioContext || new AudioContextClass()
    if (this.audioContext.state === 'suspended') await this.audioContext.resume()
  }

  ensureLegacyGetUserMedia() {
    if (navigator.getUserMedia) return
    const mediaDevices = navigator.mediaDevices
    if (!mediaDevices?.getUserMedia) {
      throw new Error('当前浏览器不支持麦克风采集，请检查浏览器版本和麦克风权限。')
    }
    navigator.getUserMedia = (constraints, onSuccess, onError) => {
      mediaDevices.getUserMedia(constraints).then(onSuccess).catch(onError || (error => {
        console.error('[语音对讲] 麦克风采集失败', error)
      }))
    }
  }

  ensureRTCPeerConnection() {
    if (typeof window.RTCPeerConnection === 'function') return

    const compatibleConstructor = window.webkitRTCPeerConnection || window.mozRTCPeerConnection
    if (typeof compatibleConstructor !== 'function') {
      throw new Error('当前浏览器未提供 WebRTC 通话组件，请升级或更换chrome浏览器后重试。')
    }

    window.RTCPeerConnection = compatibleConstructor
  }

  createRemoteMedia() {
    const video = document.createElement('video')
    video.id = `intercomRemoteAudio${++this.remoteMediaSequence}`
    video.autoplay = true
    video.playsInline = true
    video.style.cssText = 'position:fixed;width:1px;height:1px;left:-10000px;top:-10000px'
    video.addEventListener('loadedmetadata', () => video.play().catch(() => {}))
    document.body.appendChild(video)
    return video.id
  }

  clearMedia(dialog) {
    const tag = dialog?.params?.tag
    const element = tag && document.getElementById(tag)
    if (element) {
      element.pause()
      element.srcObject = null
      element.remove()
    }
  }

  get callbacks() {
    return {
      onLogin: () => {
        clearTimeout(this.loginTimer)
        state.status = '值班中'
        state.enabled = true
        state.error = ''
      },
      onLogout: () => {
        if (state.status === '连接中') return
        if (this.started) {
          state.status = '连接中断'
          state.error = '语音信令已断开，请点击重连。'
        }
      },
      onRinging: dialog => this.trackCall(dialog, 'ringing'),
      onCalling: dialog => this.trackCall(dialog, 'calling'),
      onAnswer: dialog => this.trackCall(dialog, 'active'),
      onHold: dialog => this.trackCall(dialog, 'held'),
      onRecover: dialog => this.trackCall(dialog, 'active'),
      onHangup: dialog => this.finishCall(dialog)
    }
  }

  trackCall(dialog, status) {
    if (!dialog?.callID) return
    const callId = dialog.callID
    const existing = this.calls.get(callId)
    const callerName = this.getCallerName(dialog) || '未知呼叫机'
    const callerNumber = dialog.params?.remote_caller_id_number || dialog.params?.caller_id_number || ''
    const matchedGate = this.getMatchedGate(dialog)
    const name = matchedGate?.gateName || existing?.name || callerName
    const selected = !!matchedGate
    const visible = status === 'ringing'
      ? selected
      : (existing ? (!existing.hidden || selected) : selected)
    if (!visible && !existing) {
      this.calls.set(callId, {
        dialog,
        hidden: true,
        state: status,
        name,
        callerNumber,
        callerName,
        receivedAt: Date.now(),
        ringingAt: status === 'ringing' ? Date.now() : undefined
      })
      return
    }
    this.calls.set(callId, {
      dialog,
      hidden: !visible,
      state: status,
      name,
      callerNumber,
      callerName,
      receivedAt: existing?.receivedAt || Date.now(),
      ringingAt: status === 'ringing' ? (existing?.ringingAt || Date.now()) : existing?.ringingAt,
      startedAt: status === 'active' ? (existing?.startedAt || Date.now()) : existing?.startedAt
    })
    this.publishCalls()
  }

  finishCall(dialog) {
    const callId = dialog?.callID
    if (!callId) return
    this.clearMedia(dialog)
    this.calls.delete(callId)
    const waiter = this.endWaiters.get(callId)
    if (waiter) waiter()
    this.publishCalls()
  }

  refreshVisibility() {
    this.calls.forEach(call => {
      if (call.state === 'ringing') call.hidden = !this.isSelected(call.dialog)
      else call.hidden = false
    })
    this.publishCalls()
  }

  publishCalls() {
    state.calls = [...this.calls.entries()]
      .filter(([, call]) => !call.hidden)
      .map(([callId, call]) => ({
        callId,
        name: call.name,
        callerNumber: call.callerNumber,
        callerName: call.callerName,
        status: call.state,
        receivedAt: call.receivedAt,
        ringingAt: call.ringingAt,
        startedAt: call.startedAt
      }))
      .sort((a, b) => a.receivedAt - b.receivedAt)
    this.syncRingtone()
  }

  syncRingtone() {
    const ringing = state.calls.some(call => call.status === 'ringing')
    if (ringing && !this.ringTimer) {
      this.beep()
      this.ringTimer = setInterval(() => this.beep(), 1800)
    } else if (!ringing && this.ringTimer) {
      clearInterval(this.ringTimer)
      this.ringTimer = null
    }
  }

  beep() {
    if (!this.audioContext || this.audioContext.state !== 'running') return
    const oscillator = this.audioContext.createOscillator()
    const gain = this.audioContext.createGain()
    oscillator.frequency.value = 740
    gain.gain.setValueAtTime(0.0001, this.audioContext.currentTime)
    gain.gain.exponentialRampToValueAtTime(0.12, this.audioContext.currentTime + 0.02)
    gain.gain.exponentialRampToValueAtTime(0.0001, this.audioContext.currentTime + 0.32)
    oscillator.connect(gain)
    gain.connect(this.audioContext.destination)
    oscillator.start()
    oscillator.stop(this.audioContext.currentTime + 0.33)
  }

  getActiveCallIds(exceptCallId) {
    return [...this.calls.entries()]
      .filter(([callId, call]) => callId !== exceptCallId && ['answering', 'active', 'held'].includes(call.state))
      .map(([callId]) => callId)
  }

  async answer(callId, replaceCallIds = []) {
    const call = this.calls.get(callId)
    if (!call || call.state !== 'ringing' || !this.isSelected(call.dialog)) return
    for (const activeId of replaceCallIds) await this.hangup(activeId)
    const latest = this.calls.get(callId)
    if (!latest || latest.state !== 'ringing') return
    latest.state = 'answering'
    latest.hidden = false
    this.publishCalls()
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false })
      stream.getTracks().forEach(track => track.stop())
      rtcHelper.rtcAnswer(latest.dialog, false)
    } catch (error) {
      latest.state = 'ringing'
      this.publishCalls()
      state.error = error?.message || '接听失败，请检查麦克风权限。'
    }
  }

  reject(callId) {
    return this.hangup(callId)
  }

  hangup(callId) {
    const call = this.calls.get(callId)
    if (!call) return Promise.resolve()
    call.state = 'ending'
    this.publishCalls()
    return new Promise(resolve => {
      let completed = false
      const finish = () => {
        if (completed) return
        completed = true
        clearTimeout(timer)
        this.endWaiters.delete(callId)
        const current = this.calls.get(callId)
        if (current) {
          this.clearMedia(current.dialog)
          this.calls.delete(callId)
          this.publishCalls()
        }
        resolve()
      }
      const timer = setTimeout(finish, 1600)
      this.endWaiters.set(callId, finish)
      try {
        rtcHelper.rtcHangup(callId)
      } catch (error) {
        state.error = error?.message || '结束通话失败。'
        finish()
      }
    })
  }

  async stop() {
    clearTimeout(this.loginTimer)
    if (this.started) {
      try { rtcHelper.rtcLogout() } catch (error) { console.warn('关闭语音分机失败', error) }
    }
    this.started = false
    this.calls.forEach(call => this.clearMedia(call.dialog))
    this.calls.clear()
    this.endWaiters.forEach(resolve => resolve())
    this.endWaiters.clear()
    clearInterval(this.ringTimer)
    this.ringTimer = null
    state.status = '未启动'
    state.error = ''
    state.calls = []
    state.enabled = false
  }
}

const intercomManager = new IntercomManager()

export { intercomManager, state as intercomState }
export default intercomManager
